import { useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  ImageMessage,
  StackLayout,
  HeadingText,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { useBlocker } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import { ResponsiveButtonRow } from 'components/responsive-button-row/responsive-button-row.component';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import EXIT from 'assets/exit-warning-illustration.png';
import {
  useExitWarningContext,
  useExitWarningDispatchContext,
  ReducerActionKind,
} from './exit-warning.context';

type ExitWarningProps = {
  headingText?: string;
  bodyText?: string;
  stayButtonText?: string;
  leaveButtonText?: string;
};

export const ExitWarning = ({
  headingText,
  bodyText,
  stayButtonText,
  leaveButtonText,
}: ExitWarningProps) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();
  const dirtyForms = useExitWarningContext();
  const dispatch = useExitWarningDispatchContext();
  const shouldBlockNavigation = dirtyForms.length > 0;
  const dirtyFormNames = dirtyForms.join(', ');

  // Stop user with default browser dialog when reloading or navigating away from the app
  const onBeforeUnload = useCallback(
    (ev: Event) => {
      if (shouldBlockNavigation) {
        ev.preventDefault();
      }
      return true;
    },
    [shouldBlockNavigation],
  );
  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [onBeforeUnload]);

  // Stop user with custom modal when navigating within the router
  const { state, proceed, reset } = useBlocker(shouldBlockNavigation);

  // Return the array of dirty forms to its initial state when the the component unmounts.
  useEffect(
    () => () => {
      dispatch({
        type: ReducerActionKind.removeAll,
      });
    },
    [dispatch],
  );

  return (
    <Modal.Root open={state === 'blocked'}>
      <Modal.Content
        showCloseButton={false}
        width={{
          '@initial': 640,
          '@mobile': '100%',
          '@mobileLandscape': '100%',
        }}
      >
        <StackLayout
          horizontalAlignment="end"
          css={{ padding: '$half $half $none $half' }}
        >
          <Button
            priority="tertiary"
            icon="tinyClose"
            size="small"
            hideLabel
            quiet
            onClick={reset}
          >
            {stayButtonText ||
              formatMessage({ id: 'STR_EXIT_WARNING_STAY_ON_PAGE' })}
          </Button>
        </StackLayout>
        <ImageMessage image={EXIT} imageGap="$two" imageWidth={160}>
          <StackLayout>
            <Modal.Title>
              <HeadingText
                size="xl"
                level="2"
                css={{ marginBlockEnd: '$half' }}
              >
                {headingText ||
                  formatMessage({ id: 'STR_EXIT_WARNING_HEADING' })}
              </HeadingText>
            </Modal.Title>
            <Modal.Description>
              <ParagraphText>
                {bodyText ||
                  formatMessage(
                    { id: 'STR_EXIT_WARNING_BODY' },
                    { formName: dirtyFormNames },
                  )}
              </ParagraphText>
            </Modal.Description>
            <ResponsiveButtonRow
              layoutProps={{ horizontalAlignment: 'spaceBetween' }}
              css={{
                marginBlockStart: '$oneAndHalf',
                paddingBlockEnd: isBelowTablet ? '$five' : '$none',
              }}
              primaryBtn={
                <Modal.Close>
                  <Button
                    width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                    onClick={proceed}
                  >
                    {leaveButtonText ||
                      formatMessage({ id: 'STR_EXIT_WARNING_LEAVE_PAGE' })}
                  </Button>
                </Modal.Close>
              }
              secondaryBtn={
                <Button
                  priority="secondary"
                  width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                  onClick={reset}
                >
                  {stayButtonText ||
                    formatMessage({ id: 'STR_EXIT_WARNING_STAY_ON_PAGE' })}
                </Button>
              }
            />
          </StackLayout>
        </ImageMessage>
      </Modal.Content>
    </Modal.Root>
  );
};
